import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { SocketContext } from '../context/SocketContext';
import { FocusableSection, FocusableButton, FocusableCard, useBackHandler, setFocus } from '../components/Focusable';
import { API_URL } from '../config';
import { useToast } from '../context/ToastContext';

const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;

  @media (max-width: 768px) {
    padding: 1rem;
    padding-top: 70px;
  }
`;

const MenuButton = styled.button`
  position: fixed;
  top: 20px;
  left: 20px;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #632ce4, #9d50bb);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  display: ${props => props.$show ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  z-index: 999;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(99, 44, 228, 0.4);

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 20px rgba(99, 44, 228, 0.6);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    top: 15px;
    left: 15px;
    width: 45px;
    height: 45px;
  }
`;

const MenuLine = styled.div`
  width: 25px;
  height: 3px;
  background: #fff;
  border-radius: 2px;
  transition: all 0.3s ease;
`;

const Header = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 2rem;
  position: relative;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1rem;
  }
`;

const Title = styled.h1`
  color: #000;
  font-size: 2rem;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const UploadButtonWrapper = styled.div`
  position: absolute;
  right: 0;

  @media (max-width: 768px) {
    position: static;
  }
`;

const UploadButton = styled.button`
  background: #632ce4;
  color: #fff;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  transition: background 0.3s ease;

  &:hover {
    background: #5020c0;
  }
`;

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 1rem;
    margin-top: 1rem;
  }
`;

const MediaCard = styled.div`
  background: linear-gradient(145deg, #1a1d24, #15171c);
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  border: 2px solid ${props => props.$unviewed ? '#632ce4' : 'transparent'};

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(99, 44, 228, 0.3);
  }
`;

const MediaPreview = styled.div`
  width: 100%;
  height: 200px;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    height: 150px;
  }
`;

const MediaInfo = styled.div`
  padding: 1rem;
`;

const MediaName = styled.h3`
  color: #fff;
  font-size: 0.9rem;
  margin: 0 0 0.5rem 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MediaDetails = styled.p`
  color: #888;
  font-size: 0.8rem;
  margin: 0.25rem 0;
`;

const Badge = styled.span`
  position: absolute;
  top: 10px;
  right: 10px;
  background: #632ce4;
  color: #fff;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: bold;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: #1a1d24;
  padding: 2rem;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  border: 2px solid #632ce4;

  @media (max-width: 768px) {
    padding: 1.5rem;
    width: 95%;
    max-height: 85vh;
  }
`;

const ModalTitle = styled.h2`
  color: #fff;
  margin-bottom: 1.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  margin: 0.5rem 0;
  border: 1px solid #333;
  border-radius: 5px;
  background: #15171c;
  color: #fff;
  font-size: 1rem;
  outline: none;

  &:focus {
    border-color: #632ce4;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  margin: 0.5rem 0;
  border: 1px solid #333;
  border-radius: 5px;
  background: #15171c;
  color: #fff;
  font-size: 1rem;
  outline: none;

  &:focus {
    border-color: #632ce4;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  display: block;
  width: 100%;
  padding: 2rem;
  margin: 1rem 0;
  border: 2px dashed #632ce4;
  border-radius: 8px;
  text-align: center;
  color: #888;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(99, 44, 228, 0.1);
    color: #632ce4;
  }
`;

const FileList = styled.div`
  margin: 1rem 0;
  max-height: 200px;
  overflow-y: auto;
`;

const FileItem = styled.div`
  background: #15171c;
  padding: 0.75rem;
  margin: 0.5rem 0;
  border-radius: 5px;
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RemoveButton = styled.button`
  background: #e74c3c;
  color: #fff;
  border: none;
  padding: 0.25rem 0.75rem;
  border-radius: 3px;
  cursor: pointer;
  font-size: 0.8rem;

  &:hover {
    background: #c0392b;
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const ModalButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;

  ${props => props.primary ? `
    background: #632ce4;
    color: #fff;
    &:hover {
      background: #5020c0;
    }
  ` : `
    background: transparent;
    color: #fff;
    border: 1px solid #333;
    &:hover {
      border-color: #666;
    }
  `}
`;

const Error = styled.p`
  color: #e74c3c;
  font-size: 0.9rem;
  margin: 0.5rem 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #666;
`;

const MediaViewer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
`;

const MediaViewerContent = styled.div`
  max-width: 90%;
  max-height: 90%;
  position: relative;

  img, video {
    max-width: 100%;
    max-height: 90vh;
    object-fit: contain;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(231, 76, 60, 0.8);
  color: #fff;
  border: none;
  padding: 1rem 1.5rem;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.5rem;
  z-index: 3001;

  &:hover {
    background: #e74c3c;
  }

  @media (max-width: 768px) {
    top: 15px;
    right: 15px;
    padding: 0.75rem 1rem;
    font-size: 1.2rem;
  }
`;

const DownloadButton = styled.button`
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: rgba(99, 44, 228, 0.8);
  color: #fff;
  border: none;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  z-index: 3001;

  &:hover {
    background: #632ce4;
  }

  @media (max-width: 768px) {
    bottom: 15px;
    right: 15px;
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
  }
`;

export const Photos = ({ sidebar, toggleSidebar }) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [receivedMedia, setReceivedMedia] = useState([]);
  const [viewingMedia, setViewingMedia] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [mediaUrls, setMediaUrls] = useState({});
  const { user } = useContext(AuthContext);
  const { newMediaNotification, clearMediaNotification } = useContext(SocketContext);
  const toast = useToast();

  // Gestion du back selon le contexte
  useBackHandler(() => {
    if (viewingMedia) {
      setViewingMedia(null);
    } else if (showUploadModal) {
      setShowUploadModal(false);
    }
  });

  // Focus sur la section appropriée quand le contexte change
  useEffect(() => {
    if (showUploadModal) {
      setFocus('UPLOAD_MODAL');
    } else if (viewingMedia) {
      setFocus('MEDIA_VIEWER');
    } else if (!sidebar) {
      setFocus('PHOTOS_MAIN');
    }
  }, [showUploadModal, viewingMedia, sidebar]);

  // Charger un média de manière sécurisée et créer un blob URL
  const loadSecureMediaUrl = async (mediaId) => {
    if (mediaUrls[mediaId]) return mediaUrls[mediaId];

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/media/stream/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Erreur chargement média');

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      setMediaUrls(prev => ({ ...prev, [mediaId]: url }));
      return url;
    } catch (err) {
      console.error('Erreur chargement média:', err);
      return null;
    }
  };

  // Nettoyer les blob URLs au démontage
  useEffect(() => {
    return () => {
      Object.values(mediaUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  // Charger les blob URLs pour les médias reçus
  useEffect(() => {
    receivedMedia.forEach(media => {
      if (!mediaUrls[media.id]) {
        loadSecureMediaUrl(media.id);
      }
    });
  }, [receivedMedia]);

  useEffect(() => {
    loadGroups();
    loadReceivedMedia();
  }, []);

  useEffect(() => {
    if (newMediaNotification) {
      loadReceivedMedia();
      clearMediaNotification();
    }
  }, [newMediaNotification]);

  const loadGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroups(res.data);
    } catch (err) {
      console.error('Erreur chargement groupes:', err);
    }
  };

  const loadReceivedMedia = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/media/received`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceivedMedia(res.data);
    } catch (err) {
      console.error('Erreur chargement médias:', err);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    setError('');

    if (selectedFiles.length === 0) {
      setError('Veuillez sélectionner au moins un fichier');
      return;
    }

    if (!selectedGroup) {
      setError('Veuillez sélectionner un groupe');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });
      formData.append('groupId', selectedGroup);

      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/media/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setShowUploadModal(false);
      setSelectedFiles([]);
      setSelectedGroup('');
      loadReceivedMedia(); // Recharger pour voir la photo partagée
      toast.success('Fichiers partagés avec succès !', {
        title: 'Envoi réussi'
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'envoi');
    } finally {
      setUploading(false);
    }
  };

  const handleViewMedia = async (media) => {
    setViewingMedia(media);

    if (!media.viewed) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API_URL}/api/media/mark-viewed/${media.id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        loadReceivedMedia();
      } catch (err) {
        console.error('Erreur marquage média:', err);
      }
    }
  };

  const handleDownload = async (media) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/media/download/${media.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', media.original_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Erreur téléchargement:', err);
      toast.error('Erreur lors du téléchargement', {
        title: 'Téléchargement échoué'
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isVideo = (fileType) => {
    return fileType.startsWith('video/');
  };

  const getMediaUrl = (mediaId) => {
    return mediaUrls[mediaId] || null;
  };

  const unviewedCount = receivedMedia.filter(m => !m.viewed).length;

  return (
    <FocusableSection focusKey="PHOTOS_MAIN" isFocusBoundary={showUploadModal || viewingMedia}>
      <Container>
        <FocusableButton
          as={MenuButton}
          show={!sidebar}
          onPress={toggleSidebar}
        >
          <MenuLine />
          <MenuLine />
          <MenuLine />
        </FocusableButton>

        <Header>
          <Title>Mes Photos {unviewedCount > 0 && `(${unviewedCount} nouveaux)`}</Title>
          <UploadButtonWrapper>
            <FocusableButton
              as={UploadButton}
              onPress={() => setShowUploadModal(true)}
            >
              📤 Partager des fichiers
            </FocusableButton>
          </UploadButtonWrapper>
        </Header>

        {receivedMedia.length === 0 ? (
              <EmptyState>
                <h2>Aucune photo partagée</h2>
                <p>Les photos et vidéos partagées dans vos groupes apparaîtront ici</p>
              </EmptyState>
            ) : (
              <MediaGrid>
                {receivedMedia.map((media) => (
                  <FocusableCard
                    key={media.id}
                    as={MediaCard}
                    onPress={() => handleViewMedia(media)}
                    $unviewed={!media.viewed}
                  >
                    <MediaPreview>
                      {getMediaUrl(media.id) ? (
                        isVideo(media.file_type) ? (
                          <video
                            src={getMediaUrl(media.id)}
                            style={{ pointerEvents: 'none' }}
                            preload="metadata"
                          />
                        ) : (
                          <img
                            src={getMediaUrl(media.id)}
                            alt={media.original_name}
                          />
                        )
                      ) : (
                        <div style={{ color: '#888', fontSize: '0.9rem' }}>Chargement...</div>
                      )}
                      {!media.viewed && <Badge>Nouveau</Badge>}
                    </MediaPreview>
                  </FocusableCard>
                ))}
              </MediaGrid>
            )}

      {showUploadModal && (
        <Modal onClick={() => setShowUploadModal(false)}>
          <FocusableSection
            focusKey="UPLOAD_MODAL"
            isFocusBoundary
            onClick={(e) => e.stopPropagation()}
          >
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalTitle>Partager des fichiers</ModalTitle>
              {error && <Error>{error}</Error>}

              <FocusableButton
                as={Select}
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                disabled={uploading}
              >
                <option value="">Sélectionner un groupe</option>
                {groups.map(group => (
                  <option key={group.id} value={group.id}>
                    {group.name} ({group.member_count} membres)
                  </option>
                ))}
              </FocusableButton>

              <FileInput
                type="file"
                id="file-upload"
                multiple
                accept="image/*,video/*"
                onChange={handleFileSelect}
                disabled={uploading}
              />
              <FocusableButton
                as={FileInputLabel}
                onPress={() => document.getElementById('file-upload').click()}
              >
                {selectedFiles.length === 0
                  ? '📁 Cliquez pour sélectionner des fichiers'
                  : `${selectedFiles.length} fichier(s) sélectionné(s)`}
              </FocusableButton>

              {selectedFiles.length > 0 && (
                <FileList>
                  {selectedFiles.map((file, index) => (
                    <FileItem key={index}>
                      <span>
                        {file.name} ({formatFileSize(file.size)})
                      </span>
                      <FocusableButton
                        as={RemoveButton}
                        onPress={() => removeFile(index)}
                        disabled={uploading}
                      >
                        ✕
                      </FocusableButton>
                    </FileItem>
                  ))}
                </FileList>
              )}

              <ModalButtons>
                <FocusableButton
                  as={ModalButton}
                  onPress={() => setShowUploadModal(false)}
                  disabled={uploading}
                >
                  Annuler
                </FocusableButton>
                <FocusableButton
                  as={ModalButton}
                  primary
                  onPress={handleUpload}
                  disabled={uploading}
                >
                  {uploading ? 'Envoi en cours...' : 'Envoyer'}
                </FocusableButton>
              </ModalButtons>
            </ModalContent>
          </FocusableSection>
        </Modal>
      )}

      {viewingMedia && (
        <MediaViewer onClick={() => setViewingMedia(null)}>
          <FocusableSection focusKey="MEDIA_VIEWER" isFocusBoundary>
            <FocusableButton
              as={CloseButton}
              onPress={() => setViewingMedia(null)}
            >
              ✕
            </FocusableButton>
            <MediaViewerContent onClick={(e) => e.stopPropagation()}>
              {getMediaUrl(viewingMedia.id) ? (
                isVideo(viewingMedia.file_type) ? (
                  <video
                    src={getMediaUrl(viewingMedia.id)}
                    controls
                    autoPlay
                  />
                ) : (
                  <img
                    src={getMediaUrl(viewingMedia.id)}
                    alt={viewingMedia.original_name}
                  />
                )
              ) : (
                <div style={{ color: '#fff', fontSize: '1.2rem' }}>Chargement...</div>
              )}
            </MediaViewerContent>
            <FocusableButton
              as={DownloadButton}
              onPress={() => handleDownload(viewingMedia)}
            >
              ⬇️ Télécharger
            </FocusableButton>
          </FocusableSection>
        </MediaViewer>
      )}
      </Container>
    </FocusableSection>
  );
};

export default Photos;